import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Info, BookOpen } from "lucide-react";
import {
  type USACarrier,
  type PaymentFrequency,
  usaCarrierLabels,
  usaCarrierNotes,
  getCarrierFrequencyNote,
  directRecognitionExplainer,
} from "@/lib/usaCarrierData";

interface USACarrierNotesProps {
  carrier: USACarrier;
  paymentFrequency: PaymentFrequency;
}

export function USACarrierNotes({ carrier, paymentFrequency }: USACarrierNotesProps) {
  const [drOpen, setDrOpen] = useState(false);
  const carrierNote = usaCarrierNotes[carrier];
  const frequencyNote = getCarrierFrequencyNote(carrier, paymentFrequency);
  const carrierLabel = usaCarrierLabels[carrier];

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Info className="h-4 w-4 text-primary" />
          Carrier Notes — {carrierLabel}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-foreground/80 leading-relaxed">{carrierNote}</p>

        {frequencyNote && (
          <div className="bg-muted/50 rounded-md p-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Payment Frequency Note</p>
            {frequencyNote.split('\n\n').map((para, i) => (
              <p key={i} className="text-xs text-foreground/70 leading-relaxed mt-1">{para}</p>
            ))}
          </div>
        )}

        <Collapsible open={drOpen} onOpenChange={setDrOpen}>
          <CollapsibleTrigger className="flex items-center gap-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors cursor-pointer">
            {drOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            <BookOpen className="h-3 w-3" />
            {directRecognitionExplainer.title}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 p-3 rounded-md bg-secondary text-xs text-foreground/70 leading-relaxed">
              {directRecognitionExplainer.content}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
